import * as Yup from 'yup';
import { API_BASE_URL, APP_POOL_CONFIG } from '@synergy/config';
import { Box, Stack, styled } from '@mui/material';
import { Form, Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolFormikTextfield, PoolTypography } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { LoginApi } from '../common/api/login-api';
import { PhoneLink } from '../common/components/phone-link/phone-link';
import Footer from '../common/components/footer/footer';

const Content = styled(Stack)(({ theme }) => ({
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexGrow: 1,
    paddingTop: '20vh',

    [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(8),
    },
}));

const StyledForm = styled(Form)(() => ({
    maxWidth: 300,
}));

interface ForgotPasswordData {
    email: string;
}

export const ForgotPassword = (): ReactElement => {
    const navigate = useNavigate();
    const location = useLocation();

    const forgotPasswordApi = new LoginApi(API_BASE_URL);

    const initialValues: ForgotPasswordData = {
        email: '',
    };

    const schema = Yup.object().shape({
        email: Yup.string().required('app.error.required'),
    });

    const handleBackToLogin = (): void => {
        navigate({ pathname: '/', search: location.search });
    };

    const handleSubmit = ({ email }: ForgotPasswordData) => {
        forgotPasswordApi.requestResetPassword(email);
        navigate({ pathname: email, search: location.search });
    };

    return (
        <>
            <Content>
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    <StyledForm>
                        <Stack
                            typography="body1"
                            justifyContent="flex-start"
                            textAlign="center"
                            alignSelf="flex-start"
                            gap={2}
                            sx={{ width: theme => theme.spacing(37.5) }}
                        >
                            <PoolTypography component="div" variant="h4" translationId="app.forgotPassword.title" />
                            <Box>
                                <PoolFormattedMessage
                                    id="app.forgotPassword.subtitle"
                                    values={{ poolConfigNameShort: APP_POOL_CONFIG['name_short'] }}
                                />
                            </Box>
                            <PoolFormikTextfield
                                name="email"
                                label={<FormattedMessage id="app.login.email" />}
                                type="text"
                                fullWidth={true}
                                required={true}
                                autoFocus={true}
                                autoComplete="email"
                            />
                            <Box>
                                <PoolButton variant="contained" type="submit" fullWidth={true} sx={{ mb: 2 }}>
                                    <FormattedMessage id="app.forgotPassword.resetPassword" />
                                </PoolButton>
                                <PoolButton onClick={handleBackToLogin} variant="outlined" fullWidth={true}>
                                    <FormattedMessage id="app.forgotPassword.cancel" />
                                </PoolButton>
                            </Box>
                        </Stack>
                    </StyledForm>
                </Formik>
            </Content>
            <Footer>
                <FormattedMessage id="app.footer.noEmail" />
                <Box>
                    <FormattedMessage
                        id="app.footer.questions"
                        values={{ poolConfigNameShort: APP_POOL_CONFIG['name_short'] }}
                    />{' '}
                    <PhoneLink />
                </Box>
            </Footer>
        </>
    );
};
